import { DELETE_DEAD_PROFILES_MAX_IDS, type IssueCountResponse, type LastClean } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { logger } from '@/utils/logger';

import { issueCountReceived, issueCountStarted } from '../../_shared/store/issue-count.actions';
import type {
  BrokenUrlProfile,
  CleaningRow,
  DeleteBrokenUrlsResult,
  DuplicateGroup,
  MergeGroupResult,
  MixedCurrencyExitProfile,
} from '../domain/profile-cleaning.model';
import type { CleanPlan, DeleteTotals } from '../domain/profile-cleaning.rules';
import {
  addDeleteResult,
  batchIds,
  buildCleanPlan,
  cleanSummary,
  deleteBrokenUrlsSummaryParts,
  emptyDeleteTotals,
  filterRowsByType,
  mergedSummaryParts,
  mergeErrorsNotice,
  mergeGroupNotice,
} from '../domain/profile-cleaning.rules';
import type { ProfileCleaningGateway } from '../infrastructure/profile-cleaning.gateway';
import {
  cleanRequested,
  cleanSelectedRequested,
  convertRequested,
  issueCountRequested,
  loadRequested,
  mergeGroupRequested,
  refreshRequested,
} from './profile-cleaning.actions';
import { selectRows } from './profile-cleaning.selectors';
import { type CleaningData, profileCleaningActions } from './profile-cleaning.slice';

/** Best-effort human message from a thrown error (no domain error union here). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * Read the latest clean (#619). Best-effort: it is one line of the band, and a
 * failure here must not fail the page or turn a successful clean into an error
 * snackbar; the line simply stays as it was.
 */
function* refreshLastClean() {
  const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
  try {
    const lastClean: LastClean | null = yield call(gateway.loadLastClean);
    yield put(profileCleaningActions.lastCleanSet(lastClean));
  } catch (e) {
    logger.warn('[profile-cleaning] Last clean could not be read', e);
  }
}

/**
 * WORKER: the "Profile Cleaning" sidebar badge (#753). Silent on failure, like
 * the other sidebar badges: it is a hint on every admin page, so an error
 * banner would be noise; the last known count stays. Its own task (a `put` of
 * `issueCountRequested`, never a `call` from another worker), so a failure here
 * can never turn a merge into "Merge failed" nor stop a clean.
 */
function* issueCountWorker(action: ReturnType<typeof issueCountRequested>) {
  try {
    const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
    // Shared with the Exit & Investment Review slice (#762), which keeps the
    // queue's size from the same answer.
    yield put(issueCountStarted());
    const counts: IssueCountResponse['data'] = yield call(gateway.getIssueCount, {
      fresh: action.payload?.fresh === true,
    });
    yield put(issueCountReceived(counts));
  } catch (e) {
    logger.warn('[profile-cleaning] Issue count could not be read', e);
  }
}

/**
 * The page's lists, read together: broken URLs, duplicate groups and
 * mixed-currency exits (#709). Not the profiles whose Exits & investments are to
 * complete: the slowest list of the admin has its own page since #762, and the
 * page no longer waits on it, nor reads it again after each action.
 */
function* fetchLists() {
  const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
  const [brokenUrlProfiles, duplicateGroups, mixedCurrencyProfiles]: [
    BrokenUrlProfile[],
    DuplicateGroup[],
    MixedCurrencyExitProfile[],
  ] = yield all([
    call(gateway.loadBrokenUrlProfiles),
    call(gateway.loadDuplicateGroups),
    call(gateway.loadMixedCurrencyExitProfiles),
  ]);
  return { brokenUrlProfiles, duplicateGroups, mixedCurrencyProfiles };
}

/**
 * Fetch the lists and store them silently (no loading spinner), then the
 * sidebar badge: every clean, merge, conversion and lock change comes through
 * here, and the first four can move it.
 */
function* reload() {
  const data: CleaningData = yield* fetchLists();
  yield put(profileCleaningActions.dataSet(data));
  // Fresh, not the server's 5-minute cache: the badge equals the tile (#759).
  yield put(issueCountRequested({ fresh: true }));
  // After the lists, not beside them: the clean being dated is what just ran.
  yield* refreshLastClean();
}

/** WORKER: initial load of both lists (drives the first-mount spinner). */
function* loadWorker() {
  yield put(profileCleaningActions.loadStarted());
  try {
    const data: CleaningData = yield* fetchLists();
    yield put(profileCleaningActions.loadSucceeded(data));
  } catch {
    yield put(profileCleaningActions.loadFailed());
    return;
  }
  // The rows on screen are fresh: the sidebar badge follows them (#753), counted
  // again rather than served from the server's 5-minute cache (#759).
  yield put(issueCountRequested({ fresh: true }));
  yield* refreshLastClean();
}

/**
 * Execute a clean plan: delete the broken-URL ids, merge the duplicate batches,
 * and collect each step's success part and errors independently; one failing
 * step must not swallow the other's result.
 *
 * The one path both clean buttons take. They used to differ: "Clean selected"
 * sent ids, "Clean all" called the bulk endpoints, which re-query the database
 * and act on whatever is dead *at that instant* rather than on what the dialog
 * counted. So a run confirmed as "Clean 8 profiles" could delete more, if an
 * enrichment marked new profiles dead while the page sat open; irreversibly,
 * and only visible afterwards in the snackbar (PI-77). Sending the ids is what
 * makes the announced number a promise, and it is the same rule #431 already
 * stated for the type filter: what is on screen is what gets cleaned.
 */
function* executePlan(plan: CleanPlan) {
  const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
  const parts: string[] = [];
  const errors: string[] = [];

  if (plan.brokenUrlIds.length > 0) {
    let totals: DeleteTotals = emptyDeleteTotals;
    let any = false;
    // Batched: the endpoint caps a request at DELETE_DEAD_PROFILES_MAX_IDS, and
    // "Clean all" can hand it every row on screen. try/catch per batch, so a
    // failing one keeps what its predecessors already deleted.
    for (const batch of batchIds(plan.brokenUrlIds, DELETE_DEAD_PROFILES_MAX_IDS)) {
      try {
        const res: DeleteBrokenUrlsResult = yield call(
          gateway.deleteBrokenUrlProfilesByIds,
          batch,
        );
        totals = addDeleteResult(totals, res);
        any = true;
      } catch (e) {
        errors.push(toMessage(e, 'Failed to delete profiles with a broken URL'));
      }
    }
    if (any) parts.push(...deleteBrokenUrlsSummaryParts(totals));
  }

  if (plan.mergeBatches.length > 0) {
    let merged = 0;
    const mergeErrors: string[] = [];
    // Sequential, not `all()`: each call runs a merge transaction per duplicate
    // on the backend, and the batches can touch the same rows. try/catch per
    // batch, so one failing group cannot cancel the others.
    for (const batch of plan.mergeBatches) {
      try {
        const res: MergeGroupResult = yield call(
          gateway.mergeGroup,
          batch.canonicalId,
          batch.duplicateIds,
        );
        merged += res.merged;
        if (res.errors) mergeErrors.push(...res.errors);
      } catch (e) {
        mergeErrors.push(toMessage(e, 'Failed to merge duplicates'));
      }
    }
    parts.push(...mergedSummaryParts(merged, mergeErrors.length));
    errors.push(...mergeErrorsNotice(mergeErrors));
  }

  return { parts, errors };
}

/**
 * WORKER: clean every row the active scope shows, then refresh.
 *
 * The plan covers the visible rows, all of them: the scope is the page's type
 * filter, so a clean launched while one type is on screen cannot reach the other
 * behind the user's back (#431). Canonicals are in the rows and out of the plan:
 * `buildCleanPlan` keeps them, exactly as a merge does.
 */
function* cleanWorker(action: ReturnType<typeof cleanRequested>) {
  const rows: CleaningRow[] = yield select(selectRows);
  const visible = filterRowsByType(rows, action.payload.type);
  const plan = buildCleanPlan(visible, new Set(visible.map((row) => row.id)));

  yield put(profileCleaningActions.cleanStarted());
  try {
    const { parts, errors } = yield* executePlan(plan);
    yield put(profileCleaningActions.noticeSet({ success: cleanSummary(parts), errors }));
    yield* reload();
  } finally {
    yield put(profileCleaningActions.cleanStopped());
  }
}

/**
 * WORKER: clean only the ticked rows. The plan is re-derived here from the rows
 * currently in the store, so the action only ever carries ids.
 */
function* cleanSelectedWorker(action: ReturnType<typeof cleanSelectedRequested>) {
  const rows: CleaningRow[] = yield select(selectRows);
  const plan = buildCleanPlan(rows, new Set(action.payload.ids));

  // Defensive: the button is absent when nothing ticked is actionable (a
  // selection of canonicals only). Drop the ticks and make no request.
  if (plan.brokenUrlIds.length === 0 && plan.mergeBatches.length === 0) {
    yield put(profileCleaningActions.selectionResetRequested());
    return;
  }

  yield put(profileCleaningActions.cleanStarted());
  try {
    const { parts, errors } = yield* executePlan(plan);
    yield put(profileCleaningActions.noticeSet({ success: cleanSummary(parts), errors }));
    yield* reload();
  } finally {
    yield put(profileCleaningActions.selectionResetRequested());
    yield put(profileCleaningActions.cleanStopped());
  }
}

/** WORKER: merge a single duplicate group inline, then refresh. */
function* mergeGroupWorker(action: ReturnType<typeof mergeGroupRequested>) {
  const { canonicalId, duplicateIds, groupKey } = action.payload;
  const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
  yield put(profileCleaningActions.mergeStarted(groupKey));
  try {
    const res: MergeGroupResult = yield call(gateway.mergeGroup, canonicalId, duplicateIds);
    const notice = mergeGroupNotice(res);
    yield put(
      profileCleaningActions.noticeSet({
        success: notice.success,
        errors: notice.error ? [notice.error] : [],
      }),
    );
    yield* reload();
  } catch (e) {
    yield put(
      profileCleaningActions.noticeSet({ success: null, errors: [toMessage(e, 'Merge failed')] }),
    );
  } finally {
    yield put(profileCleaningActions.mergeStopped());
  }
}

/**
 * WORKER: save a conversion (#732). On success the dialog closes and the lists
 * are re-read, so the profile leaves the table; on failure the dialog stays open
 * with what the admin typed, and the reason shows as an error.
 */
function* convertWorker(action: ReturnType<typeof convertRequested>) {
  const { profileId, request } = action.payload;
  const gateway: ProfileCleaningGateway = yield getContext('profileCleaningGateway');
  yield put(profileCleaningActions.convertStarted());
  try {
    yield call(gateway.convertExitCurrencies, profileId, request);
  } catch (e) {
    yield put(
      profileCleaningActions.noticeSet({
        success: null,
        errors: [toMessage(e, 'Conversion failed')],
      }),
    );
    return;
  } finally {
    yield put(profileCleaningActions.convertStopped());
  }
  yield put(profileCleaningActions.convertDialogClosed());
  yield put(
    profileCleaningActions.noticeSet({
      success: `Exit amounts converted to ${request.currency}`,
      errors: [],
    }),
  );
  // The conversion is saved: a failed re-read only leaves the row until the next load.
  try {
    yield* reload();
  } catch (e) {
    logger.warn('[profile-cleaning] Refresh after a conversion failed', e);
  }
}

/** WORKER: silent re-read after a lock change. A failure keeps the rows on screen. */
function* refreshWorker() {
  try {
    yield* reload();
  } catch (e) {
    logger.warn('[profile-cleaning] Refresh after a lock change failed', e);
  }
}

/** WATCHERS. */
export function* profileCleaningSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(refreshRequested.type, refreshWorker),
    takeLatest(cleanRequested.type, cleanWorker),
    takeLatest(cleanSelectedRequested.type, cleanSelectedWorker),
    takeLatest(mergeGroupRequested.type, mergeGroupWorker),
    takeLatest(convertRequested.type, convertWorker),
    takeLatest(issueCountRequested.type, issueCountWorker),
  ]);
}
